import { createAction, createReducer } from '@reduxjs/toolkit';

import { OPERATOR_PANEL_INFORMATION_SCREENS } from '@components/operatorPanel/constants/operatorPanel.constants';
import type { OPTabInReducer } from '@components/operatorPanel/interfaces';
import type { RootState } from '@store/types';
import type { AppDispatch } from '@reducers/utils/types';
import type { OperatorPanelState, UpdateTabsCountParams, IncrementTabCountParams, DecrementTabCountParams } from './types';
import { ImageDocumentStoreItem } from '@v2/components/imageHandling/types/types';

// Re-export types for consumers
export type { OperatorPanelState, UpdateTabsCountParams, IncrementTabCountParams, DecrementTabCountParams } from './types';

// === Initial State ===

const initState: OperatorPanelState = {
  screenToBackFromJobPerspective: null,
  currentBatchId: null,
  isNoteAdding: false,
  tabsLoading: false,
  tabs: [
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.NcFiles,
      title: 'NC File',
      icon: 'markdown',
      count: 0,
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.Jobs,
      title: 'Jobs',
      icon: 'jobsList',
      iconSelected: 'jobsListSelected',
      count: 0,
      requires: { machinePerspective: { erp: true, notUnknownNcFile: true } },
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.Setup,
      title: 'Setup Sheet',
      icon: 'setup',
      iconSelected: 'setupSelected',
      count: 0,
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.Tools,
      title: 'Tools',
      icon: 'tools',
      iconSelected: 'toolsSelected',
      count: 0,
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.Notes,
      title: 'Notes',
      icon: 'editCircle',
      iconSelected: 'editCircleSelected',
      count: 0,
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS['2DDrawing'],
      title: '2D Drawing',
      icon: 'twoDDrawing',
      count: 0,
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS['3DDrawing'],
      title: '3D Drawing',
      icon: 'threeDDrawing',
      iconSelected: 'threeDDrawingSelected',
      count: 0,
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.Clamping,
      title: 'Clamping Image',
      icon: 'clampingImage',
      count: 0,
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.Part,
      title: 'Part Image',
      icon: 'partImage',
      iconSelected: 'partImageSelected',
      count: 0,
      requires: { machinePerspective: { notUnknownNcFile: true } },
    },
    // {
    //   value: OPERATOR_PANEL_INFORMATION_SCREENS.Material,
    //   title: 'Material',
    //   icon: 'material',
    // 	 count: 0,
    // },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.ProductionOrder,
      title: 'Production Order',
      icon: 'productionOrder',
      iconSelected: 'conveyorSelected',
      count: 0,
      requires: { machinePerspective: { erp: true, notUnknownNcFile: true } },
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.Measurement,
      title: 'Measurement',
      icon: 'measurements',
      iconSelected: 'measurementsSelected',
      count: 0,
    },
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.AllDocuments,
      title: 'All Documents',
      icon: 'files',
      iconSelected: 'filesSelected',
      count: 0,
      noResultText: 'Document',
      noResultIcon: 'document',
    },
    // Search section separator
    {
      isSeparator: true,
      title: 'Search',
    },
    // NC Files Search
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.SearchNCFiles,
      title: 'NC Files',
      icon: 'markdown',
      count: 0,
    },
    // NC File History
    {
      value: OPERATOR_PANEL_INFORMATION_SCREENS.SearchHistory,
      title: 'NC File History',
      icon: 'ncFileSearchHistory',
      count: 0,
    },
  ],
};

// === Selectors ===

export const selectImagesDocuments = (state: RootState) => state.operatorPanel.imagesDocuments;
export const selectTabs = (state: RootState) => state.operatorPanel.tabs;
export const selectCurrentBatchId = (state: RootState) => state.operatorPanel.currentBatchId;
export const selectTabsLoading = (state: RootState) => state.operatorPanel.tabsLoading;
export const selectScreenToBackFromJobPerspective = (state: RootState) => state.operatorPanel.screenToBackFromJobPerspective;

// === Actions ===

const updateTabsAction = createAction<OPTabInReducer[]>('operatorPanel/updateTabsAction');
const setImagesDocumentsAction = createAction<ImageDocumentStoreItem[]>('operatorPanel/setImagesDocumentsAction');
const setScreenToBackFromJobPerspectiveAction = createAction<OPERATOR_PANEL_INFORMATION_SCREENS | null>(
  'operatorPanel/setScreenToBackFromJobPerspectiveAction'
);
const setCurrentBatchIdAction = createAction<string | null>('operatorPanel/setCurrentBatchIdAction');
const setIsNoteAddingAction = createAction<boolean>('operatorPanel/setIsNoteAddingAction');
const resetTabsCountAction = createAction('operatorPanel/resetTabsCountAction');
const setTabsLoadingAction = createAction<boolean>('operatorPanel/setTabsLoadingAction');

// === Thunks ===

export const setOperatorPanelTabs = (tabs: OPTabInReducer[]) => (dispatch: AppDispatch) => {
  dispatch(updateTabsAction(tabs));
};

export const setImagesDocuments = (images: ImageDocumentStoreItem[]) => (dispatch: AppDispatch) => {
  dispatch(setImagesDocumentsAction(images));
};

export const setScreenToBackFromJobPerspective = (screen: OPERATOR_PANEL_INFORMATION_SCREENS | null) => (dispatch: AppDispatch) => {
  dispatch(setScreenToBackFromJobPerspectiveAction(screen));
};

export const setCurrentBatchId = (batchId: string | null) => (dispatch: AppDispatch) => {
  dispatch(setCurrentBatchIdAction(batchId));
};

const updateTabsCount =
  ({ updatedBy, action, tabName }: UpdateTabsCountParams) =>
  (dispatch: AppDispatch, getState: () => RootState): OPTabInReducer[] => {
    const { tabs } = getState().operatorPanel;
    return tabs.map((t) =>
      t.value === tabName ? { ...t, count: action === 'increment' ? (t.count ?? 0) + updatedBy : Math.max((t.count ?? 0) - updatedBy, 0) } : t
    );
  };

export const incrementTabCount =
  ({ incrementBy = 1, tabName }: IncrementTabCountParams) =>
  (dispatch: AppDispatch) => {
    const updatedTabs = dispatch(updateTabsCount({ updatedBy: incrementBy, tabName, action: 'increment' }));
    dispatch(updateTabsAction(updatedTabs));
  };

export const decrementTabCount =
  ({ decrementBy = 1, tabName }: DecrementTabCountParams) =>
  (dispatch: AppDispatch) => {
    const updatedTabs = dispatch(updateTabsCount({ updatedBy: decrementBy, tabName, action: 'decrement' }));
    dispatch(updateTabsAction(updatedTabs));
  };

export const setIsNoteAdding = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setIsNoteAddingAction(state));
};

export const resetTabsCounts = () => (dispatch: AppDispatch) => {
  dispatch(resetTabsCountAction());
};

export const setTabsLoading = (state: boolean) => (dispatch: AppDispatch) => {
  dispatch(setTabsLoadingAction(state));
};

// === Reducer ===

export default createReducer<OperatorPanelState>(initState, (builder) => {
  builder
    .addCase(updateTabsAction, (state, action) => {
      return { ...state, tabs: action.payload };
    })
    .addCase(setTabsLoadingAction, (state, action) => {
      return { ...state, tabsLoading: action.payload };
    })
    .addCase(setImagesDocumentsAction, (state, action) => {
      return { ...state, imagesDocuments: action.payload };
    })
    .addCase(setScreenToBackFromJobPerspectiveAction, (state, action) => {
      return { ...state, screenToBackFromJobPerspective: action.payload };
    })
    .addCase(setCurrentBatchIdAction, (state, action) => {
      return { ...state, currentBatchId: action.payload };
    })
    .addCase(setIsNoteAddingAction, (state, action) => {
      return { ...state, isNoteAdding: action.payload };
    })
    .addCase(resetTabsCountAction, (state) => {
      return {
        ...state,
        tabs: state.tabs.map((tab) => ({ ...tab, count: 0 })),
      };
    });
});
